import { FC, useEffect, useState } from 'react'
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { FiltersState, OneAlert, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Download, PersonMinus, PersonPlus } from '@factorialco/f0-react/icons/app'
import {
  ProjectForSelectFragment,
  ProjectManagementProjectWorkerFilter,
  ProjectPeopleDataCollectionDocument,
  ProjectPeopleDataCollectionItemFragment,
  RateProcessedDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

import { getPeopleCount } from 'modules/project_management/lib/constants'
import { formatProjectHours } from 'modules/project_management/lib/formatHours'
import { costRateCell } from 'modules/project_management/lib/utils'

import { ProjectWorkersPanelData } from './types'

let hasItemsWithoutBillRate = false

type GenericInFilter = {
  label: string
  type: 'in'
  options: {
    cache: boolean
    options: () => Promise<Array<{ label: string; value: boolean }>>
  }
}

const resolveFilters = (
  filters: FiltersState<{
    assigned: GenericInFilter
  }>
) => {
  const filter: ProjectManagementProjectWorkerFilter = {}

  const assigned = filters?.assigned?.map(Boolean) || []
  if (assigned.length === 1) {
    filter.assigned = { eq: assigned[0] }
  }

  return filter
}
interface ProjectPeopleDataCollectionProps {
  projectCurrency: string
  canAssignEmployees: boolean
  canAssignSingleEmployee: boolean
  canExportData: boolean
  canManageProject: boolean
  canSeeLaborCosts: boolean
  canSeeRates: boolean
  canSeeSpendings: boolean
  onClick: (newData: ProjectWorkersPanelData) => void
}

const ProjectPeopleDataCollection: FC<ProjectPeopleDataCollectionProps> = ({
  projectCurrency,
  canAssignEmployees,
  canAssignSingleEmployee,
  canExportData,
  canManageProject,
  canSeeLaborCosts,
  canSeeRates,
  canSeeSpendings,
  onClick,
}) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { hasExpenses, hasFlexibleTracking, project } = useOutletContext<{
    hasExpenses: boolean
    hasFlexibleTracking: boolean
    project: ProjectForSelectFragment
  }>()

  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const { data: rateProcessedData } = useSubscription(RateProcessedDocument)

  useEffect(() => {
    if (rateProcessedData?.projectmanagementRateProcessed?.rate?.id) {
      const processedRate = rateProcessedData.projectmanagementRateProcessed.rate
      if (!processedRate.processing) {
        setRefreshTrigger((prev) => prev + 1)
      }
    }
  }, [rateProcessedData])

  const itemOnClick = canManageProject
    ? (item: ProjectPeopleDataCollectionItemFragment) => () => {
        const rate = item.ratesConnection?.nodes?.[0]
        onClick({
          projectWorkerId: item.id,
          billingRateCents: rate?.billingRateCents,
          chargingCostCents: rate?.chargingCostCents ?? item.defaultRateCost?.chargingCostCents,
          profitabilityPercentageCents: rate?.profitabilityPercentageCents,
          referenceBillingRateCents: rate?.referenceRate?.billingRateCents,
          referenceProfitMarginCents: rate?.referenceRate?.profitabilityPercentageCents,
          ratesCurrency: rate?.currency,
          rateId: rate?.id,
          employeeAccessAvatar: item.employee?.access?.avatar,
          employeeFullName: item.employee?.fullName,
          imputedMinutes: item.inputedMinutes,
          laborCostCents: item.laborCostCents,
          spendingCostCents: item.spendingCostCents,
          projectCurrency: projectCurrency,

          canSeeLaborCosts: canSeeLaborCosts,
          canSeeRates: canSeeRates,
          canSeeSpendings: canSeeSpendings,
        })
      }
    : undefined

  const source = useGraphqlDataCollectionSource(
    {
      document: ProjectPeopleDataCollectionDocument,
      mapData: (data) => {
        const nodes = data.projectManagement.projectWorkersConnection.nodes

        // Check if there are any items without bill rate
        if (canSeeRates) {
          hasItemsWithoutBillRate = nodes.some(
            (item: ProjectPeopleDataCollectionItemFragment) =>
              !item.ratesConnection?.nodes?.[0]?.billingRateCents
          )
        } else {
          hasItemsWithoutBillRate = false
        }

        return nodes
      },
      mapVariables: ({ filters, search, sortings }) => ({
        projectId: project.id,
        employeeName: search,
        filter: resolveFilters(filters),
        includeCost: canSeeLaborCosts,
        includeInputedMinutes: true,
        includeRates: canSeeRates,
        hasSpendings: canSeeSpendings,
        sortOrder: sortings?.[0],
      }),
      mapPagination: (data) => ({
        total: data.projectManagement.projectWorkersConnection.totalCount ?? 0,
      }),
      totalItemSummary: (totalItems) => getPeopleCount(totalItems),
      itemOnClick,
      itemActions:
        canAssignSingleEmployee && canManageProject
          ? ({ assigned, employee, id }) => {
              const employeeId = employee?.id
              if (!employeeId) return []

              return [
                assigned
                  ? {
                      label: i18n.t(
                        'project_management.table.project_employees.menu_links.unassign'
                      ),
                      icon: PersonMinus,
                      onClick: () => {
                        navigate(
                          navigation.projects.id.details.employees.unassignEmployee.employeeId.projectWorkerId.fullPath(
                            {
                              id: project.id,
                              employeeId,
                              projectWorkerId: id,
                            }
                          )
                        )
                      },
                    }
                  : {
                      label: i18n.t('project_management.table.project_employees.menu_links.assign'),
                      icon: PersonPlus,
                      onClick: () => {
                        navigate(
                          navigation.projects.id.details.employees.assignEmployee.employeeId.projectWorkerId.fullPath(
                            {
                              id: project.id,
                              employeeId,
                              projectWorkerId: id,
                            }
                          )
                        )
                      },
                    },
              ]
            }
          : undefined,
      primaryActions: () =>
        canAssignEmployees && canManageProject
          ? {
              label: i18n.t('project_management.details.assign_employees'),
              icon: Add,
              onClick: () => {
                track(
                  trackingEvents.projectManagement.projectWorker.assignEmployeesToProjectStart,
                  {
                    project_id: project.id,
                  }
                )

                navigate(
                  navigation.projects.id.details.employees.workers.fullPath({
                    id: project.id,
                  })
                )
              },
            }
          : undefined,
      secondaryActions: () =>
        canExportData
          ? [
              {
                label: i18n.t('project_management.table.projects_list.table_options.export.title'),
                description: i18n.t(
                  'project_management.table.projects_list.table_options.export.description'
                ),
                icon: Download,
                onClick: () => {
                  track(trackingEvents.projectManagement.projects.downloadProjectsReportStart)
                  navigate(
                    navigation.projects.id.details.employees.export.fullPath({ id: project.id }),
                    { state: { projectForSelect: project } }
                  )
                },
              },
            ]
          : undefined,
      filters: {
        assigned: {
          label: i18n.t('project_management.table.project_employees.headers.status'),
          type: 'in',
          options: {
            options: [
              {
                label: i18n.t(
                  'project_management.table.project_employees.assignation_tags.assigned'
                ),
                value: true,
              },
              {
                label: i18n.t(
                  'project_management.table.project_employees.assignation_tags.not_assigned'
                ),
                value: false,
              },
            ],
          },
        },
      },
      search: {
        enabled: true,
      },
      sortings: {
        full_name: {
          label: i18n.t('project_management.table.project_employees.headers.name'),
        },
        assigned: {
          label: i18n.t('project_management.table.project_employees.headers.status'),
        },
      },
      defaultSortings: {
        field: 'assigned',
        order: 'desc',
      },
    },
    [refreshTrigger]
  )

  return (
    <>
      {canSeeRates && hasItemsWithoutBillRate && (
        <OneAlert
          variant="warning"
          title={i18n.t(
            'project_management.people_and_rates.sidepanel.alerts.people_without_bill_rate.title'
          )}
          description={i18n.t(
            'project_management.people_and_rates.sidepanel.alerts.people_without_bill_rate.description'
          )}
          link={{
            label: i18n.t(
              'project_management.people_and_rates.sidepanel.alerts.people_without_bill_rate.link'
            ),
            href: `${navigation.projects.jobsAndRates.list.fullPath()}?openAddSidepanel=true`,
          }}
        />
      )}
      <Box paddingTop={canSeeRates ? 's24' : 's0'}>
        <OneDataCollection
          emptyStates={{
            'no-data': {
              title: i18n.t(
                'project_management.table.project_employees.no_employees_message.title'
              ),
              description: i18n.t(
                'project_management.table.project_employees.no_employees_message.description'
              ),
            },
          }}
          source={source}
          visualizations={[
            {
              type: 'table',
              options: {
                columns: compact([
                  {
                    label: i18n.t('project_management.table.project_employees.headers.name'),
                    render: ({ employee }: ProjectPeopleDataCollectionItemFragment) => ({
                      type: 'person',
                      value: {
                        firstName: employee?.firstName || '',
                        lastName: employee?.lastName || '',
                        src: employee?.access?.avatar
                          ? buildUrl(employee?.access?.avatar)
                          : undefined,
                      },
                    }),
                    sorting: 'full_name' as const,
                  },
                  {
                    label: i18n.t('project_management.table.project_employees.headers.role'),
                    render: ({ employee }: ProjectPeopleDataCollectionItemFragment) => ({
                      type: 'text',
                      value:
                        employee?.referenceContract?.jobCatalogTreeLevel?.ancestor?.name ||
                        employee?.referenceContract?.jobCatalogLevel?.roleName ||
                        '-',
                    }),
                  },
                  {
                    label: i18n.t('project_management.table.project_employees.headers.level'),
                    render: ({ employee }: ProjectPeopleDataCollectionItemFragment) => ({
                      type: 'text',
                      value:
                        employee?.referenceContract?.jobCatalogTreeLevel?.name ||
                        employee?.referenceContract?.jobCatalogLevel?.name ||
                        '-',
                    }),
                  },
                  canSeeRates
                    ? {
                        label: i18n.t(
                          'project_management.table.project_employees.headers.cost_rate.label'
                        ),
                        info: i18n.t(
                          'project_management.table.project_employees.headers.cost_rate.tooltip_message'
                        ),
                        align: 'right',
                        render: ({
                          ratesConnection,
                          defaultRateCost,
                          employee,
                          processingRate,
                        }: ProjectPeopleDataCollectionItemFragment) => {
                          if (processingRate?.processing) {
                            return {
                              type: 'status',
                              value: {
                                status: 'neutral',
                                label: i18n.t(
                                  'project_management.jobs_and_rates.processing_status'
                                ),
                              },
                            }
                          }

                          const defaultCost = defaultRateCost?.chargingCostCents
                          const defaultCurrency = employee?.legalEntity?.currency

                          const rate = ratesConnection?.nodes?.[0]
                          const defaultRate =
                            defaultCost && defaultCurrency
                              ? {
                                  chargingCostCents: defaultCost,
                                  currency: defaultCurrency,
                                }
                              : undefined

                          return costRateCell(rate, defaultRate, locale)
                        },
                      }
                    : undefined,
                  canSeeRates
                    ? {
                        label: i18n.t(
                          'project_management.table.project_employees.headers.bill_rate.label'
                        ),
                        info: i18n.t(
                          'project_management.table.project_employees.headers.bill_rate.tooltip_message'
                        ),
                        align: 'right',
                        render: ({
                          ratesConnection,
                          processingRate,
                        }: ProjectPeopleDataCollectionItemFragment) => {
                          if (processingRate?.processing) {
                            return {
                              type: 'status',
                              value: {
                                status: 'neutral',
                                label: i18n.t(
                                  'project_management.jobs_and_rates.processing_status'
                                ),
                              },
                            }
                          }
                          return {
                            type: 'text',
                            value:
                              isCurrency(projectCurrency) &&
                              ratesConnection?.nodes?.[0]?.billingRateCents
                                ? MoneyWithCurrency({
                                    amount: ratesConnection?.nodes?.[0]?.billingRateCents ?? 0,
                                    currency: ratesConnection?.nodes?.[0]?.currency,
                                    optionalDecimals: true,
                                    locale,
                                  })
                                : '-',
                          }
                        },
                      }
                    : undefined,
                  canSeeRates
                    ? {
                        label: i18n.t(
                          'project_management.table.project_employees.headers.profit_margin.label'
                        ),
                        info: i18n.t(
                          'project_management.table.project_employees.headers.profit_margin.tooltip_message'
                        ),
                        align: 'right',
                        render: ({
                          ratesConnection,
                          processingRate,
                        }: ProjectPeopleDataCollectionItemFragment) => {
                          if (processingRate?.processing) {
                            return {
                              type: 'status',
                              value: {
                                status: 'neutral',
                                label: i18n.t(
                                  'project_management.jobs_and_rates.processing_status'
                                ),
                              },
                            }
                          }
                          return {
                            type: 'text',
                            value:
                              ratesConnection?.nodes?.[0]?.profitabilityPercentageCents != null
                                ? `${ratesConnection.nodes[0].profitabilityPercentageCents / 100}%`
                                : '-',
                          }
                        },
                      }
                    : undefined,
                  {
                    label: i18n.t(
                      'project_management.table.project_employees.headers.tracked_hours.label'
                    ),
                    align: 'right',
                    info: i18n.t(
                      'project_management.table.project_employees.headers.tracked_hours.tooltip_message'
                    ),
                    render: ({ inputedMinutes }: ProjectPeopleDataCollectionItemFragment) => ({
                      type: 'text',
                      value: formatProjectHours(inputedMinutes),
                    }),
                  },
                  canSeeLaborCosts
                    ? {
                        label: i18n.t(
                          'project_management.table.project_employees.headers.labor_cost.label'
                        ),
                        align: 'right',
                        info: i18n.t(
                          'project_management.table.project_employees.headers.labor_cost.tooltip_message'
                        ),
                        render: ({ laborCostCents }: ProjectPeopleDataCollectionItemFragment) => ({
                          type: 'text',
                          value:
                            laborCostCents && isCurrency(projectCurrency)
                              ? MoneyWithCurrency({
                                  amount: laborCostCents,
                                  currency: projectCurrency,
                                  locale,
                                })
                              : '-',
                        }),
                      }
                    : null,
                  canSeeRates
                    ? {
                        label: i18n.t(
                          'project_management.table.project_employees.headers.labor_bill.label'
                        ),
                        info: i18n.t(
                          'project_management.table.project_employees.headers.labor_bill.tooltip_message'
                        ),
                        align: 'right',
                        render: ({
                          inputedMinutes,
                          ratesConnection,
                        }: ProjectPeopleDataCollectionItemFragment) => {
                          const inputedHours = inputedMinutes ? inputedMinutes / 60 : 0
                          const billingRateCents =
                            ratesConnection?.nodes?.[0]?.billingRateCents ?? 0
                          const laborBillCents = Math.round(billingRateCents * inputedHours)
                          return {
                            type: 'text',
                            value:
                              isCurrency(projectCurrency) && laborBillCents
                                ? MoneyWithCurrency({
                                    amount: laborBillCents,
                                    currency: ratesConnection?.nodes?.[0]?.currency,
                                    optionalDecimals: true,
                                    locale,
                                  })
                                : '-',
                          }
                        },
                      }
                    : undefined,
                  canSeeSpendings
                    ? {
                        label: i18n.t(
                          'project_management.table.project_employees.headers.spendings.label'
                        ),
                        align: 'right',
                        info: i18n.t(
                          'project_management.table.project_employees.headers.spendings.tooltip_message'
                        ),
                        render: ({
                          spendingCostCents,
                        }: ProjectPeopleDataCollectionItemFragment) => ({
                          type: 'text',
                          value:
                            spendingCostCents && isCurrency(projectCurrency)
                              ? MoneyWithCurrency({
                                  amount: spendingCostCents,
                                  currency: projectCurrency,
                                  locale,
                                })
                              : '-',
                        }),
                      }
                    : null,
                  {
                    label: i18n.t('project_management.table.project_employees.headers.status'),
                    render: ({ assigned }: ProjectPeopleDataCollectionItemFragment) => ({
                      type: 'status',
                      value: {
                        label: assigned
                          ? i18n.t(
                              'project_management.table.project_employees.assignation_tags.assigned'
                            )
                          : i18n.t(
                              'project_management.table.project_employees.assignation_tags.not_assigned'
                            ),
                        status: assigned ? 'info' : 'neutral',
                      },
                    }),
                    sorting: 'assigned' as const,
                  },
                ]),
              },
            },
          ]}
        />
      </Box>
      <Outlet context={{ hasExpenses, hasFlexibleTracking }} />
    </>
  )
}

export default ProjectPeopleDataCollection
